// frontend/src/components/StrengthAddModal.js
// Modal component for adding strength data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * StrengthAddModal Component
 * Provides a dedicated interface for adding strength data to the flowsheet
 */
const StrengthAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one strength parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Strength data saved successfully!');

        // Refresh vitals banner (in case strength data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save strength data');
      }
    } catch (error) {
      console.error('Error saving strength data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save strength data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define strength parameters based on parameters.md
  const strengthFields = [
    { name: 'biometrics_strength_handgrip_right_1', label: 'Handgrip Strength - Right Trial 1', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_right_2', label: 'Handgrip Strength - Right Trial 2', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_left_1', label: 'Handgrip Strength - Left Trial 1', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_left_2', label: 'Handgrip Strength - Left Trial 2', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_right_avg', label: 'Handgrip Strength - Right Average', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_left_avg', label: 'Handgrip Strength - Left Average', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_handgrip_normative_status', label: 'Handgrip Strength - Normative Status', inputType: 'Input', placeholder: 'e.g., Normal, Below Average' },
    { name: 'biometrics_strength_handgrip_deviation_percent', label: 'Handgrip Strength - Deviation', units: '%', inputType: 'InputNumber', placeholder: '%', rules: [{ type: 'number', message: 'Please enter a valid number' }] },
    { name: 'biometrics_strength_handgrip_relative_strength', label: 'Handgrip Strength - Relative', units: 'kg/kg body weight', inputType: 'InputNumber', placeholder: 'kg/kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_strength_pushups_completed', label: 'Push-ups Completed', inputType: 'InputNumber', placeholder: 'count', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Strength Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#eb2f96', borderColor: '#eb2f96' }}
        >
          Save Strength Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Strength Entry"
          description="Enter muscular strength assessment results and grip strength measurements for the selected date and time. These assessments help track muscular strength and functional capacity changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="strength_add_form">
        <Title level={5}>Strength Assessment Parameters</Title>
        
        {/* Handgrip Strength Trials */}
        <Divider orientation="left">Handgrip Strength Trials</Divider>
        <Row gutter={16}>
          {strengthFields.slice(0, 4).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Handgrip Strength Analysis */}
        <Divider orientation="left">Handgrip Strength Analysis</Divider>
        <Row gutter={16}>
          {strengthFields.slice(4, 9).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                {field.inputType === 'InputNumber' ? (
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder={field.placeholder}
                    min={field.rules?.[0]?.min}
                    max={field.rules?.[0]?.max}
                    precision={field.name.includes('relative') ? 3 : 1}
                  />
                ) : (
                  <Input 
                    placeholder={field.placeholder}
                  />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Additional Strength Tests */}
        <Divider orientation="left">Additional Strength Tests</Divider>
        <Row gutter={16}>
          {strengthFields.slice(9).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={0}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">Test Instructions</Divider>
        <Alert
          message="Strength Test Guidelines"
          description={
            <div>
              <p><strong>Handgrip Strength:</strong> Use a calibrated dynamometer. Patient should be seated with arm at 90° angle. Record best of two trials for each hand.</p>
              <p><strong>Normative Status:</strong> Compare results to age and gender-specific norms (e.g., Normal, Above Average, Below Average, Poor).</p>
              <p><strong>Relative Strength:</strong> Handgrip strength divided by body weight, providing a normalized strength measure.</p>
              <p><strong>Push-ups Test:</strong> Record maximum number of push-ups completed with proper form until exhaustion.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default StrengthAddModal;
